import { COLORS, Col, FlexView, Row } from '@cuemath/comps';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LAYOUT from 'constants/layout';
import { Button, Dropdown, Separator, Text } from '@cuemath/web-comps';
import styled from 'styled-components';
import { useAssignCSMPatch, useCSMGetAll } from '../../api/csm';
import { closeModal } from 'actions/modal';
import { CSM_ASSIGNMENT_REASONS_OPTIONS } from '../../constants/csm';
import { useDispatch } from 'react-redux';
import { invalidateTeacherGet } from 'actions/teacher-v2';
import { useTeacherGet } from 'actions/teacher-v2';
import PropTypes from 'prop-types';
import { getUserFullName } from '../../utils';

const { GAP, GUTTER } = LAYOUT;

const LabelCol = styled(Col)`
  width: 150px;
  padding-right: ${GUTTER}px;
`;

const teacherGetAPIEmbed = {
  embed: ['creds', 'psm_info'],
};

const CSMAssignmentModal = ({ teacherId }) => {
  const dispatch = useDispatch();
  const [error, setError] = useState(null);
  const [formFields, setFormFields] = useState({
    csm: '',
    reason: '',
  });
  const { data: teacherDetails } = useTeacherGet(teacherId, teacherGetAPIEmbed);
  const { rm_data: rmData } = teacherDetails || {};
  const { id: rmId } = rmData ?? {};

  useEffect(() => {
    if (!formFields.csm && rmId) {
      setFormFields(prevForm => ({
        ...prevForm,
        csm: rmId,
      }));
    }
  }, [formFields.reason, rmId]);

  const onAssignCSMComplete = (errorMsg, data) => {
    const { id } = data ?? {};
    if (id) {
      invalidateTeacherGet(teacherId, teacherGetAPIEmbed);
      dispatch(closeModal());
    } else {
      setError(errorMsg || 'something went wrong');
    }
  };

  const { patch: assignCSMPatch, isProcessing: isPatchProcessing } = useAssignCSMPatch({
    onComplete: onAssignCSMComplete,
  });

  const {
    data: listOfCSM,
    getAll: getListOfCSM,
    isProcessing: isLoading,
  } = useCSMGetAll();

  useEffect(() => {
    getListOfCSM();
  }, [getListOfCSM]);

  const handleChange = useCallback(({ key, value }) => {
    setFormFields(prevForm => ({
      ...prevForm,
      [key]: value,
    }));
  }, []);

  const handleSubmit = useCallback(() => {
    setError('');
    const { csm, reason } = formFields;

    const payload = {
      user_type: 'TEACHER',
      relationship_manager_id: csm,
      relationship_manager_change_reason: reason,
    };
    assignCSMPatch(teacherId, payload);
  }, [formFields, teacherId]);

  const options = useMemo(
    () =>
      (listOfCSM ?? []).map(({ first_name, last_name, id }) => ({
        label: getUserFullName(first_name, last_name),
        value: id,
        id,
      })),
    [listOfCSM],
  );

  const enableSubmit = !isPatchProcessing && formFields?.csm && formFields?.reason;

  return (
    <FlexView gutter={GUTTER} gap={GAP}>
      <Text variant="h6">Assign CSM</Text>
      <Separator height={24} />
      {isLoading ? (
        <Text variant="body1Regular">Loading...</Text>
      ) : (
        <>
          <Row alignItems="center">
            <LabelCol gutter={0}>
              <Text variant="body1Regular">Select a CSM</Text>
            </LabelCol>
            <Col auto gutter={0}>
              <Dropdown
                onChange={value => handleChange({ key: 'csm', value })}
                value={formFields.csm}
                options={options}
                optionsWidth="450px"
                width="100%"
                height="200px"
                type="primary"
              />
            </Col>
          </Row>
          <Row alignItems="center">
            <LabelCol gutter={0}>
              <Text variant="body1Regular">Reason</Text>
            </LabelCol>
            <Col auto gutter={0}>
              <Dropdown
                onChange={value => handleChange({ key: 'reason', value })}
                value={formFields.reason}
                options={CSM_ASSIGNMENT_REASONS_OPTIONS}
                optionsWidth="450px"
                width="100%"
                height="200px"
                type="primary"
              />
            </Col>
          </Row>
          <Separator height={GUTTER * 6} />
          <FlexView alignItems="center">
            <Button
              isBusy={isPatchProcessing}
              label="Assign"
              size="small"
              onClick={handleSubmit}
              isDisabled={!enableSubmit}
            />
          </FlexView>
        </>
      )}
      {error && (
        <FlexView alignItems="center">
          <Separator height={8} />
          <Text variant="body1Regular" color={COLORS.RED}>
            {error}
          </Text>
        </FlexView>
      )}
    </FlexView>
  );
};
CSMAssignmentModal.propTypes = {
  teacherId: PropTypes.string,
};
export default CSMAssignmentModal;
